import React, {useState, useRef, useEffect, useContext} from 'react';
import {StatusCode} from 'grpc-web';
import PulseLoader from "react-spinners/PulseLoader";

import {TimeSeriesRequest} from "../goveeenergymonitoringpb/internal/proto/goveeenergymonitoring_pb"
import {
  ResponsiveContainer
} from "recharts";
import SmartPlugHeader, {
  CalculateAverageMonthlyCost, CalculateBucketedData, GetBucketedMetadata
} from "./SmartPlugHeader";
import SmartPlugAreaChart from "./SmartPlugAreaChart";
import {AuthContext} from "../contexts/AuthContext";
import {ZoomContext} from "../contexts/ZoomContext";
import SmartPlugSideBarChart from "./SmartPlugSideBarChart";
import SmartPlugExtendedSummaryStatistics from "./SmartPlugExtendedSummaryStatistics";
import {useMediaQuery} from "react-responsive";
import moment from "moment";

const tickLookup = [3600*1000*24*182, 3600*1000*24*63, 3600*1000*24*30, 3600*1000*24*14, 3600*1000*24*7, 3600*1000*24*2, 3600*1000*24, 3600*1000*12, 3600*1000*8, 3600*1000*6, 3600*1000*4, 3600*1000*3, 3600*1000*2, 60*60*1000, 45*60*1000, 30*60*1000, 20*60*1000, 10*60*1000, 5*60*1000, 2*60*1000, 60*1000, 40*1000, 30*1000, 20*1000, 10*1000, 5*1000, 4*1000, 2*1000, 1000, 500, 200, 100, 50];

const SmartPlugExtended = ({client, device, onDragZoom, style, targetPoints, showSignalLabel}) => {
  const [data, setData] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [bucketedSamples, setBucketedSamples] = useState(null);
  const [isLoadingChart, setIsLoadingChart] = useState(true);
  const [isTemperatureEnabled, setIsTemperatureEnabled] = useState(true);
  const [isHumidityEnabled, setIsHumidityEnabled] = useState(true);
  const [isBatteryEnabled, setIsBatteryEnabled] = useState(false);
  const { getAccessToken } = useContext(AuthContext);
  const { since, until } = useContext(ZoomContext);
  // console.log("Creating SmartPlug, device", device.toObject(), zoomIntervalMillis, new Date(since),"<->", new Date(until));

  const requestIdRef = useRef(0);
  const timeoutRef = useRef(null);

  const onLegendClick = (e) => {
    // Toggle opposite series such that clicking on the legend focuses on that series
    if (e.target.name === "temperature-legend") {
      setIsHumidityEnabled(!isHumidityEnabled);
    } else if (e.target.name === "humidity-legend") {
      setIsTemperatureEnabled(!isTemperatureEnabled);
    } else if (e.target.name === "battery-legend") {
      setIsBatteryEnabled(!isBatteryEnabled);
    }
  };

  useEffect(()=>{
    let cancelled = false;
    const requestId = ++requestIdRef.current;

    clearTimeout(timeoutRef.current);
    setIsLoadingChart(false);

    const accessToken = getAccessToken();
    const metadata = {"Authorization": "Bearer " + accessToken}
    if (!accessToken) {
      return () => {
        cancelled = true;
      };
    }

    // Start delayed loading timer
    timeoutRef.current = setTimeout(() => {
      if (!cancelled && requestId === requestIdRef.current) {
        console.log("Loading timer for extended request", requestId, "setting IsLoadingChart true");
        setIsLoadingChart(true);
      }
    }, 100);

    // console.log("Fetching timeseries temperature data for",
    //     device.toObject(), new Date(since), "<->", new Date(until),
    //     "with token", accessToken);
    const tsRequest = new TimeSeriesRequest(
        [device.getId(), since, until, targetPoints || 250])

    client.getSamplesWithMetadata(tsRequest, metadata, (err, response) => {
      if (cancelled || requestId !== requestIdRef.current) return;

      clearTimeout(timeoutRef.current);
      setIsLoadingChart(false);

      if (err) {
        if (err.code === StatusCode.UNAUTHENTICATED) {
          // Don't bother retrying and having burst of retry attempts.
          console.error(
              "Received auth error getting extended measurements. Don't bother retrying, let device list refresh to the retry",
              err)
          return;
        }

        // No toast error message this level because it might be a burst
        console.error("Received error getting batch of extended measurements", err);
      } else {
        const seriesMetadata = response.getMetadataMap();
        const samples = response.getSamplesList();
        const bsamples = response.getBucketedsamples();

        console.log("Extended received", samples.length, "extended measurements for device", device.getId());

        setData(samples);
        setMetadata(seriesMetadata);
        setBucketedSamples(bsamples);
      }
    });

    // Cleanup function to clear the timer if the component unmounts
    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  },[since, until]);

  const formattedData = data.map((o, idx) => ({
    "timestamp": o.getTime(),
    "timepoweredonseconds": o.getTimePoweredOnSeconds(),
    "accumulatedwatts": o.getAccumulatedPowerDeciWatts()/10,
    "averagecostpermonth": CalculateAverageMonthlyCost(o),
    // "cents": CalculateCentsCost(o, idx > 0 ? data[idx-1] : null),
    "cents": o.getCentiWatts()/100/1000*21,
    "volts": o.getCentiVolts()/100,
    "amps": o.getCentiAmps()/100,
    "watts": o.getCentiWatts()/100,
    "signalstrength": o.getSignalStrength(),
  }));

  // const hourlyData = CalculateHourlyData(data, since, until);
  const formattedBucketedData = CalculateBucketedData(bucketedSamples, since, until);
  const bucketedStatistics = GetBucketedMetadata(bucketedSamples, since, until);
  // console.log("Hourly data", hourlyData);

  const isPortraitMobile = useMediaQuery({
    query: '(max-width: 700px)'
  });

  if (isLoadingChart) {
    return <>
      <SmartPlugHeader client={client} device={device} data={data} metadata={metadata} showSignalLabel={showSignalLabel} showExpand={false} onDragZoom={onDragZoom} onLegendClick={onLegendClick}/>
      <div className="device-chart" style={style}>
        <ResponsiveContainer width='100%' height='100%' style={{'display': 'flex', 'justifyContent': 'center', 'alignItems': 'center'}}>
          <PulseLoader speedMultiplier={0.8} color="#629af0"/>
        </ResponsiveContainer>
      </div>
    </>
  }

  const displaySideCharts = !isPortraitMobile && formattedBucketedData.length > 0;

  const leftCellsWidth = displaySideCharts ? "10%" : "100%";
  const mainWidth = displaySideCharts ? "55%" : "100%";
  const sideWidth = displaySideCharts ? "20%" : "100%";
  const summaryWidth = "15%";

  var yAxisDomainPower=[0,20];
  var yAxisDomainCost=[0,1];
  if (formattedData.length > 0) {
    const centiwatts = metadata.get("centiwatts");
    yAxisDomainPower = [Math.min(yAxisDomainPower[0], centiwatts.getSeriesMin() / 100),
      Math.max(yAxisDomainPower[1], centiwatts.getSeriesMax() / 100)];
    yAxisDomainPower[0] = yAxisDomainPower[0] - (yAxisDomainPower[0] % 20);
    yAxisDomainPower[1] = (yAxisDomainPower[1] + 20) - (yAxisDomainPower[1] % 20);

    yAxisDomainCost = [Math.min(yAxisDomainCost[0], centiwatts.getSeriesMin() /100/1000*21),
      Math.max(yAxisDomainCost[1], centiwatts.getSeriesMax() /100/1000*21)];
    yAxisDomainCost[0] = yAxisDomainCost[0] - (yAxisDomainCost[0] % 2);
    yAxisDomainCost[1] = (yAxisDomainCost[1] + 2) - (yAxisDomainCost[1] % 2);
  }

  return (
      <>
        <SmartPlugHeader client={client} device={device} data={data} metadata={metadata} showSignalLabel={showSignalLabel} showExpand={false} onDragZoom={onDragZoom} onLegendClick={onLegendClick}/>
        <div className="device-chart" style={style}>
          <div style={{minWidth: 350, height: '100%', display: "flex" }}>
            {displaySideCharts &&
                <ResponsiveContainer width={leftCellsWidth} height='100%' minWidth={220} style={{margin: 10}}>
                  {formattedBucketedData.length > 0 &&
                      <div style={{
                        justifyContent: "center",
                        textAlign: "center",
                        alignItems: "center",
                        verticalAlign: "center",
                        height: "50%"
                      }}>
                        <div className="energy-summary-title"
                             style={{height: "5%", marginBottom: "20px"}}>
                          Summmary
                        </div>

                        <div
                            className="energy-summary-statistics-gauge-container" style={{width: "100%", minHeight: 120, height: "10%"}}>
                          <div
                              className="energy-summary-statistics-gauge-label">Average
                            power
                          </div>
                          <div
                              className="energy-summary-statistics-gauge-value">
                            {Number(
                                metadata.get(
                                    "centiwatts").getSeriesAvg()
                                / 100).toFixed(1)} W
                          </div>
                        </div>
                        <div
                            className="energy-summary-statistics-gauge-container" style={{width: "100%", minHeight: 120, height: "10%"}}>
                          <div
                              className="energy-summary-statistics-gauge-label">
                            Max power @ {moment(metadata.get(
                              "centiwatts").getSeriesMaxTime()).format(
                              'MM/DD h:mma')}
                          </div>
                          <div
                              className="energy-summary-statistics-gauge-value">
                            {Number(metadata.get(
                                "centiwatts").getSeriesMax() / 100).toFixed(
                                1)} W
                          </div>
                        </div>
                        <div
                            className="energy-summary-statistics-gauge-container" style={{width: "100%", minHeight: 120, height: "10%"}}>
                          <div
                              className="energy-summary-statistics-gauge-label">
                            Average cost/day
                          </div>
                          <div
                              className="energy-summary-statistics-gauge-value">
                            ${Number(
                              metadata.get(
                                  "centiwatts").getSeriesAvg()
                              / 100 * 24 / 1000 * 0.21).toFixed(2)}
                          </div>
                        </div>
                        <div
                            className="energy-summary-statistics-gauge-container" style={{width: "100%", minHeight: 120}}>
                          <div
                              className="energy-summary-statistics-gauge-label">
                            {bucketedStatistics.getLatestTrend().getIntervalName()} power
                            is
                            trending
                          </div>
                          <div
                              className="energy-summary-statistics-gauge-value">
                            {bucketedStatistics.getLatestTrend().getTrendDescription()} by {Number(
                              bucketedStatistics.getLatestTrend().getPercentChange()).toFixed(
                              2)}%
                          </div>
                        </div>
                      </div>
                  }
                </ResponsiveContainer>
            }
            <ResponsiveContainer width={mainWidth} height='100%'>
              <div className="energy-summary-title" style={{height: "5%"}}>
                Real-time Power and Cost
              </div>
              <SmartPlugAreaChart device={device}
                                  formattedData={formattedData}
                                  dataKey="watts"
                                  seriesName="watts"
                                  seriesMetadata={metadata}
                                  areaName="Watts"
                                  areaUnit="W"
                                  yAxisName="Power (W)"
                                  yAxisDomain={yAxisDomainPower}
                                  color="#ff780a"
                                  onDragZoom={onDragZoom}
                                  containerHeight="47%"
                                  style={style}/>
              <SmartPlugAreaChart device={device}
                                  formattedData={formattedData}
                                  dataKey="cents"
                                  seriesName="cents"
                                  seriesMetadata={metadata}
                                  areaName="Cents/hr"
                                  areaUnit="¢"
                                  yAxisName="Cents/hr (¢)"
                                  yAxisDomain={yAxisDomainCost}
                                  yAxisFormatter={(value) => value.toFixed(1)}
                                  color="#629af0"
                                  onDragZoom={onDragZoom}
                                  containerHeight="48%"
                                  style={style}/>
            </ResponsiveContainer>
            {formattedBucketedData.length > 0 &&
              <ResponsiveContainer width={sideWidth} height='100%'>
                  <div className="energy-summary-title" style={{height: "5%"}}>
                    Real-time Hourly
                  </div>
                  <SmartPlugSideBarChart containerHeight="47%"
                                       device={device}
                                       data={data}
                                       formattedData={formattedBucketedData}
                                       metadata={metadata}
                                       metadataName="hourlykwh"
                                       seriesName="totalwatts" unit="kWh"
                                       unitLong="kWh" stroke="#ff780a"
                                       onDragZoom={onDragZoom}/>
                  <SmartPlugSideBarChart containerHeight="48%"
                                         device={device}
                                         data={data}
                                         formattedData={formattedBucketedData}
                                         metadata={metadata}
                                         metadataName="hourlydollars"
                                         seriesName="totalcents" unit="¢"
                                         unitLong="cost" stroke="#629af0"
                                         onDragZoom={onDragZoom}/>
              </ResponsiveContainer>
            }
            {!isPortraitMobile &&
                <>
                  <ResponsiveContainer width={summaryWidth} height='100%'
                                       minWidth={180}>
                    <SmartPlugExtendedSummaryStatistics containerHeight="50%"
                                                        client={client}
                                                        device={device}
                                                        title="Daily History"
                                                        queryIntervalMillis={1000*3600*24*7*4}
                                                        bucketIntervalMillis={1000*3600*24}/>
                    <SmartPlugExtendedSummaryStatistics containerHeight="50%"
                                                        client={client}
                                                        device={device}
                                                        title="Weekly History"
                                                        queryIntervalMillis={1000*3600*24*7*26}
                                                        bucketIntervalMillis={1000*3600*24*7}/>
                  </ResponsiveContainer>
                </>
            }
          </div>
          <div style={{minWidth: 400, display: "flex" }}>

          </div>
        </div>
      </>
  );
};

export default SmartPlugExtended;

